import React, { FC, useState } from "react"
import { FormHelperText } from "@mui/material"
import Button from "../Button"
import { IUiProps } from "../../../../types/uiProps"

interface IProps {
    onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void,
    onBlur?: () => void
    name?: string,
    // Every caller uses this for a real file input - kept only so
    // existing call sites that still pass type="file" don't need to
    // change.
    type?: string,
    accept?: string,
    placeholder?: string,
    uiProps?: IUiProps,
    isError?: boolean,
    errorMessage?: string,
    isRequired?: boolean,
    disabled?: boolean
}

export const InputFile: FC<IProps> = ({
    name,
    accept,
    onChange,
    errorMessage,
    isError,
    onBlur,
    placeholder,
    uiProps,
    isRequired,
    disabled,
}) => {
    // Native <input type="file"> can't be a controlled component (its
    // value can't be set programmatically), so the chosen file's name is
    // tracked here purely for display, mirroring what the browser's own
    // file input UI used to show.
    const [fileName, setFileName] = useState("");

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFileName(e.target.files?.[0]?.name ?? "");
        onChange?.(e);
        // Callers reject invalid files by alerting and clearing the
        // input themselves (e.target.value = null) - mirror that
        // rejection in the displayed name too.
        if (!e.target.value) {
            setFileName("");
        }
    };

    return (
        <div style={uiProps as React.CSSProperties}>
            {placeholder && (
                <label
                    style={{
                        display: "block",
                        marginBottom: "6px",
                        fontSize: "0.75rem",
                        color: isError ? "#e8464e" : "rgba(0, 0, 0, 0.6)",
                    }}
                >
                    {placeholder}
                    {isRequired && <span style={{ color: "#e8464e" }}> *</span>}
                </label>
            )}
            <div style={{ display: "inline-flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
                <Button
                    styleType={isError ? "danger" : "primary"}
                    component="label"
                    width="auto"
                    height="34px"
                    noMarginTop
                    disabled={disabled}
                >
                    Choose File
                    <input
                        type="file"
                        hidden
                        name={name}
                        onChange={handleChange}
                        onBlur={onBlur}
                        disabled={disabled}
                        accept={accept || ""}
                    />
                </Button>
                <span style={{ fontSize: "0.875rem", color: "rgba(0, 0, 0, 0.6)" }}>
                    {fileName || "No file chosen"}
                </span>
            </div>
            {isError && <FormHelperText error>{errorMessage}</FormHelperText>}
        </div>
    );
};

export default InputFile;
